import {
  DeleteIcon,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  IconButton,
  MenuItem,
  PercentageIcon,
  PlusIcon,
  RadioGroup,
  RupeeIcon,
  Select,
  Show,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "components";
import { FeePlansForm } from "interfaces/Revenue";
import { isNaN } from "lodash";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { numberToCurrency } from "utils";
import { validationForPercentage } from "utils/helpers";
import {
  Cell,
  FEE_PARAMETER,
  FEE_TYPE,
  SUBSCRIPTION_FREQUENCY,
  TableBodyCell,
} from "../utils";

type DiscountType = "Amount" | "Percentage";

const calculateAmounts = ({
  planFee = 0,
  discountAmount = 0,
  discountType = "Amount",
}: {
  planFee: number;
  discountAmount: number;
  discountType: DiscountType;
}) => {
  const discount =
    discountType === "Percentage"
      ? planFee * (discountAmount / 100)
      : discountAmount;

  const taxableAmount = Math.max(planFee - discount, 0);
  const gstAmount = taxableAmount * 0.18;
  const totalAmount = taxableAmount + gstAmount;

  return { taxableAmount, gstAmount, totalAmount };
};

const SubscriptionFeeForm = () => {
  const form = useFormContext<FeePlansForm>();

  const watchSubscriptionIsFree = form.watch("subscriptionFee.isFree");
  const feeType = form.watch("subscriptionFee.feeType");

  const isAumBased = feeType === "AUM Based";
  const isFlatFee = feeType === "Flat Fee";

  const { fields, remove, append } = useFieldArray({
    control: form.control,
    name: "subscriptionFee.subscriptionPlan",
  });

  const option = (index: number) => {
    form.watch(`subscriptionFee.subscriptionPlan.${index}.frequency`);
    return SUBSCRIPTION_FREQUENCY.filter(
      (data) =>
        !form
          .getValues("subscriptionFee.subscriptionPlan")
          ?.map((item) => item.frequency)
          .includes(data.value),
    );
  };

  return (
    <Grid2 container mt={2} spacing={3}>
      <Grid2
        size={12}
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography fontSize={14} fontWeight={700} color=" #20272A">
          Subscription Fee
        </Typography>
      </Grid2>

      <Grid2 size={6} container spacing={3}>
        <Grid2 size={12}>
          <Controller
            name="subscriptionFee.isFree"
            control={form.control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ gap: 1 }}
                control={
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      form.clearErrors("subscriptionFee.subscriptionPlan");
                    }}
                  />
                }
                label={
                  <Typography
                    fontSize={13}
                    fontWeight={600}
                    color="var(--grey-900)"
                  >
                    Make This a Free Plan
                  </Typography>
                }
                labelPlacement="start"
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="subscriptionFee.name"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                value={field?.value ?? ""}
                placeholder="Product Name"
                inputLabel={"Product Name"}
                tooltipTitle="This name will appear on the invoices generated for your clients."
              />
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="subscriptionFee.hsnSacCode"
            control={form.control}
            rules={{
              required: {
                value: !watchSubscriptionIsFree,
                message: "Please enter HSN code",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                value={field?.value ?? ""}
                requiredField
                placeholder="HSN Code"
                inputLabel={"HSN Code"}
                tooltipTitle="This code will appear on invoices to classify your product for tax compliance."
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={12}>
          <Controller
            name="subscriptionFee.feeType"
            control={form.control}
            rules={{
              required: {
                value: !watchSubscriptionIsFree,
                message: "Please select a Fee Type",
              },
            }}
            render={({ field }) => (
              <FormControl>
                <FormLabel
                  sx={{ fontSize: 13, fontWeight: 600, color: "#222" }}
                >
                  Fee Type
                </FormLabel>
                <RadioGroup
                  row
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    form.setValue(
                      "subscriptionFee.subscriptionPlan",
                      e.target.value === "Flat Fee"
                        ? [
                            {
                              frequency: "",
                              planFee: "",
                              discountAmount: "",
                              discountType: "Amount",
                              taxableAmount: "",
                              gstAmount: "",
                              totalAmount: "",
                            },
                          ]
                        : [],
                    );
                  }}
                >
                  {FEE_TYPE.map((item) => {
                    return (
                      <FormControlLabel
                        key={item.value}
                        value={item.value}
                        control={<GKRadio disableLeftPadding />}
                        label={item.name}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            )}
          />
        </Grid2>

        {/* AUM Based Fee Type Fields */}
        <Show.When isTrue={isAumBased && !watchSubscriptionIsFree}>
          <Grid2 size={6}>
            <Controller
              name="subscriptionFee.feeParameters"
              control={form.control}
              rules={{
                required: {
                  value: isAumBased && !watchSubscriptionIsFree,
                  message: "Please select a Fee Parameter",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Fee Parameter"
                  disableClearable={false}
                  options={FEE_PARAMETER.filter((param) =>
                    [
                      "Opening AUM Current Value",
                      "Opening AUM Portfolio Value",
                    ].includes(param.value),
                  )}
                  onChange={(e, val) => field.onChange(val?.value)}
                  value={
                    FEE_PARAMETER.find(
                      (dataItem) => dataItem.value === field.value,
                    ) || ""
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name="subscriptionFee.fee"
              control={form.control}
              rules={{
                required: {
                  value: isAumBased && !watchSubscriptionIsFree,
                  message: "Please enter Fee %",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  value={field?.value ?? ""}
                  requiredField
                  inputLabel="Fee %"
                  type="number"
                  onChange={(
                    event: React.ChangeEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    if (event.target instanceof HTMLInputElement) {
                      const { valueAsNumber } = event.target;
                      field.onChange(valueAsNumber);
                    }
                  }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name="subscriptionFee.availableFrequency"
              control={form.control}
              rules={{
                required: {
                  value: isAumBased && !watchSubscriptionIsFree,
                  message: "Please select Available Frequencies",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Available Subscription Frequencies"
                  disableClearable={false}
                  multiple
                  options={SUBSCRIPTION_FREQUENCY || []}
                  onChange={(event, val) => {
                    field.onChange(
                      val.map((option: { value: string }) => option.value),
                    );
                  }}
                  value={
                    SUBSCRIPTION_FREQUENCY?.filter(
                      (option: { value: string }) =>
                        field?.value?.includes(option?.value),
                    ) || []
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="subscriptionFee.minimumInvestment"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  value={field?.value ?? ""}
                  inputLabel="Minimum Investment"
                  type="number"
                  onChange={(
                    event: React.ChangeEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    if (event.target instanceof HTMLInputElement) {
                      const { valueAsNumber } = event.target;
                      field.onChange(valueAsNumber);
                    }
                  }}
                />
              )}
            />
          </Grid2>
        </Show.When>
      </Grid2>

      <Show.When isTrue={isFlatFee && !watchSubscriptionIsFree}>
        <Grid2 size={12}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ "&.MuiTableRow-head th": { p: "12px" } }}>
                  <Cell>FREQUENCY</Cell>
                  <Cell>PLAN FEES</Cell>
                  <Cell>DISCOUNT</Cell>
                  <Cell align="right">TAXABLE AMOUNT</Cell>
                  <Cell align="right">GST (%)</Cell>
                  <Cell align="right">GRAND TOTAL</Cell>
                  <Cell align="center">ACTIONS</Cell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields?.map((row, index) => {
                  return (
                    <TableRow
                      key={row?.id}
                      sx={{ "&.MuiTableRow-head th": { p: "12px" } }}
                    >
                      <TableBodyCell sx={{ width: "15.83%" }}>
                        <Controller
                          name={`subscriptionFee.subscriptionPlan.${index}.frequency`}
                          control={form.control}
                          rules={{ required: !watchSubscriptionIsFree }}
                          render={({ field, fieldState: { error } }) => (
                            <GKSearchSelect
                              {...field}
                              size="small"
                              requiredField
                              placeholder="Frequency"
                              disabled={watchSubscriptionIsFree}
                              disableClearable
                              options={option(index) || []}
                              onChange={(e, val) => {
                                field.onChange(val?.value);
                              }}
                              value={
                                SUBSCRIPTION_FREQUENCY.find(
                                  (dataItem) => dataItem.value === field.value,
                                ) || ""
                              }
                              getOptionLabel={(option) => option.name || option}
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                      </TableBodyCell>
                      <TableBodyCell sx={{ width: "15.83%" }}>
                        <Controller
                          name={`subscriptionFee.subscriptionPlan.${index}.planFee`}
                          control={form.control}
                          rules={{ required: !watchSubscriptionIsFree }}
                          render={({ field, fieldState: { error } }) => (
                            <GKTextField
                              {...field}
                              value={field?.value ?? ""}
                              size={"small"}
                              disabled={watchSubscriptionIsFree}
                              requiredField
                              type="number"
                              placeholder="PlanFee"
                              onChange={(e) => {
                                const planFee = Number(e.target.value);
                                field.onChange(isNaN(planFee) ? 0 : planFee);

                                const discountAmount = Number(
                                  form.getValues(
                                    `subscriptionFee.subscriptionPlan.${index}.discountAmount`,
                                  ),
                                );
                                const discountType = form.getValues(
                                  `subscriptionFee.subscriptionPlan.${index}.discountType`,
                                );

                                const {
                                  taxableAmount,
                                  gstAmount,
                                  totalAmount,
                                } = calculateAmounts({
                                  planFee,
                                  discountAmount,
                                  discountType: discountType as DiscountType,
                                });

                                form.setValue(
                                  `subscriptionFee.subscriptionPlan.${index}.taxableAmount`,
                                  taxableAmount,
                                );
                                form.setValue(
                                  `subscriptionFee.subscriptionPlan.${index}.gstAmount`,
                                  gstAmount,
                                );
                                form.setValue(
                                  `subscriptionFee.subscriptionPlan.${index}.totalAmount`,
                                  totalAmount,
                                );
                              }}
                              error={!!error}
                            />
                          )}
                        />
                      </TableBodyCell>
                      <TableBodyCell sx={{ width: "15.83%" }}>
                        <Controller
                          name={`subscriptionFee.subscriptionPlan.${index}.discountAmount`}
                          control={form.control}
                          rules={{
                            validate: (value) => {
                              const discountType = form.watch(
                                `subscriptionFee.subscriptionPlan.${index}.discountType`,
                              ) as "Amount" | "Percentage";

                              const planFee = Number(
                                form.watch(
                                  `subscriptionFee.subscriptionPlan.${index}.planFee`,
                                ),
                              );

                              const discount = Number(value);

                              if (discountType === "Percentage") {
                                if (discount >= 100) {
                                  return "Percentage cannot exceed 100%";
                                }
                              }

                              if (discountType === "Amount") {
                                if (discount >= planFee) {
                                  return "Discount cannot exceed Plan Fee";
                                }
                              }

                              return true;
                            },
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <GKTextField
                              {...field}
                              value={field?.value ?? ""}
                              sx={{ "& .MuiInputBase-root": { pl: 0 } }}
                              size="small"
                              placeholder="Discount"
                              disabled={watchSubscriptionIsFree}
                              type="number"
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                const discount = isNaN(value) ? 0 : value;

                                const discountType = form.getValues(
                                  `subscriptionFee.subscriptionPlan.${index}.discountType`,
                                );
                                const planFee = Number(
                                  form.getValues(
                                    `subscriptionFee.subscriptionPlan.${index}.planFee`,
                                  ),
                                );

                                field.onChange(
                                  discountType === "Percentage"
                                    ? validationForPercentage(discount)
                                    : discount,
                                );

                                const {
                                  taxableAmount,
                                  gstAmount,
                                  totalAmount,
                                } = calculateAmounts({
                                  planFee,
                                  discountAmount: discount,
                                  discountType: discountType as DiscountType,
                                });

                                form.setValue(
                                  `subscriptionFee.subscriptionPlan.${index}.taxableAmount`,
                                  taxableAmount,
                                );
                                form.setValue(
                                  `subscriptionFee.subscriptionPlan.${index}.gstAmount`,
                                  gstAmount,
                                );
                                form.setValue(
                                  `subscriptionFee.subscriptionPlan.${index}.totalAmount`,
                                  totalAmount,
                                );
                              }}
                              error={!!error}
                              slotProps={{
                                input: {
                                  startAdornment: (
                                    <Controller
                                      control={form.control}
                                      name={`subscriptionFee.subscriptionPlan.${index}.discountType`}
                                      render={({ field }) => (
                                        <Select
                                          {...field}
                                          size="small"
                                          onChange={(e) => {
                                            field.onChange(e.target.value);
                                            form.setValue(
                                              `subscriptionFee.subscriptionPlan.${index}.discountAmount`,
                                              "",
                                            );

                                            const planFee = Number(
                                              form.getValues(
                                                `subscriptionFee.subscriptionPlan.${index}.planFee`,
                                              ),
                                            );

                                            const {
                                              taxableAmount,
                                              gstAmount,
                                              totalAmount,
                                            } = calculateAmounts({
                                              planFee,
                                              discountAmount: 0,
                                              discountType: e?.target
                                                ?.value as DiscountType,
                                            });

                                            form.setValue(
                                              `subscriptionFee.subscriptionPlan.${index}.taxableAmount`,
                                              taxableAmount,
                                            );
                                            form.setValue(
                                              `subscriptionFee.subscriptionPlan.${index}.gstAmount`,
                                              gstAmount,
                                            );
                                            form.setValue(
                                              `subscriptionFee.subscriptionPlan.${index}.totalAmount`,
                                              totalAmount,
                                            );
                                          }}
                                          sx={{
                                            "& .MuiSelect-select": {
                                              display: "flex",
                                              alignItems: "center",
                                            },
                                          }}
                                        >
                                          <MenuItem value="Amount">
                                            <RupeeIcon height={16} width={16} />
                                          </MenuItem>
                                          <MenuItem value="Percentage">
                                            <PercentageIcon
                                              height={16}
                                              width={16}
                                            />
                                          </MenuItem>
                                        </Select>
                                      )}
                                    />
                                  ),
                                },
                              }}
                            />
                          )}
                        />
                      </TableBodyCell>
                      <TableBodyCell align="right" sx={{ width: "15.83%" }}>
                        <Typography>
                          {numberToCurrency(
                            form.watch(
                              `subscriptionFee.subscriptionPlan.${index}.taxableAmount`,
                            ),
                          )}
                        </Typography>
                      </TableBodyCell>
                      <TableBodyCell align="right" sx={{ width: "15.83%" }}>
                        <Typography>
                          18% (
                          {numberToCurrency(
                            form.watch(
                              `subscriptionFee.subscriptionPlan.${index}.gstAmount`,
                            ),
                          )}
                          )
                        </Typography>
                      </TableBodyCell>
                      <TableBodyCell align="right" sx={{ width: "15.83%" }}>
                        <Typography>
                          {numberToCurrency(
                            form.watch(
                              `subscriptionFee.subscriptionPlan.${index}.totalAmount`,
                            ),
                          )}
                        </Typography>
                      </TableBodyCell>
                      <TableBodyCell
                        align="center"
                        style={{
                          padding: "12px",
                          borderRight: "1px solid var(--neutral-100)",
                          width: "5%",
                        }}
                      >
                        <IconButton
                          onClick={() => remove(index)}
                          color="error"
                          disabled={fields?.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableBodyCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <GKButton
            sx={{ mt: 2 }}
            startIcon={<PlusIcon />}
            onClick={() =>
              append({
                frequency: "",
                planFee: "",
                discountAmount: "",
                discountType: "Amount",
                taxableAmount: "",
                gstAmount: "",
                totalAmount: "",
              })
            }
          >
            Add Row
          </GKButton>
        </Grid2>
      </Show.When>
    </Grid2>
  );
};

export default SubscriptionFeeForm;
